import React from "react";
import {
  Popper,
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Fade,
  Divider,
  Tooltip,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BookmarkAddIcon from "@mui/icons-material/BookmarkAdd";
import BookmarkAddedIcon from "@mui/icons-material/BookmarkAdded";
import TranslateIcon from '@mui/icons-material/Translate';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import TextToSpeechButton from '@/components/common/TextToSpeechButton';

/**
 * Floating popover that appears next to a text selection.
 * Shows the Vietnamese translation and a "Save to memories" button.
 */
const TranslationPopover = ({
  anchorEl,
  open,
  word,
  translation,
  wordTranslation,
  contextMeaning,
  sentenceTranslation,
  pronunciation,
  saved,
  loading,
  saving,
  error,
  onSave,
  onClose,
}) => {
  const navigate = useNavigate();

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="top"
      transition
      modifiers={[
        { name: "offset", options: { offset: [0, 8] } },
        { name: "preventOverflow", options: { padding: 8 } },
        {
          name: "flip",
          options: { fallbackPlacements: ["bottom", "right", "left"] },
        },
      ]}
      style={{ zIndex: 2000 }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={180}>
          <Paper
            data-vocab-lookup-popover="true"
            elevation={8}
            sx={{
              minWidth: 240,
              maxWidth: 320,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid rgba(33, 150, 243, 0.2)",
              boxShadow: "0 8px 24px rgba(33, 150, 243, 0.18)",
              background: "#fff",
            }}
            onMouseDown={(e) => {
              // Prevent the global selection-clearing logic from firing
              e.stopPropagation();
            }}
          >
            {/* Header: word + close */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.75}
                sx={{ minWidth: 0 }}
              >
                <TranslateIcon sx={{ color: "#2196F3", fontSize: 18 }} />
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  sx={{
                    color: "#1565C0",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {word}
                </Typography>
                {pronunciation && (
                  <Typography variant="caption" color="text.secondary">
                    /{pronunciation}/
                  </Typography>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={0.25}>
                {word && (
                  <Tooltip title="Pronounce">
                    <span>
                      <TextToSpeechButton
                        text={word}
                        size="small"
                        color="primary"
                        sx={{ p: 0.5 }}
                      />
                    </span>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={onClose} sx={{ p: 0.25 }}>
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Body */}
            {loading ? (
              <Box display="flex" alignItems="center" gap={1} py={0.5}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Translating…
                </Typography>
              </Box>
            ) : error ? (
              <Alert severity="warning" sx={{ py: 0.25, fontSize: "0.8rem" }}>
                {error}
              </Alert>
            ) : (
              <Box>
                {/* Primary: context-resolved meaning (preferred) */}
                <Typography
                  variant="body2"
                  sx={{ color: "#263238", lineHeight: 1.5, fontWeight: 500 }}
                >
                  {translation || "—"}
                </Typography>

                {/* Full-sentence translation for comprehension */}
                {sentenceTranslation && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      bgcolor: "rgba(33, 150, 243, 0.06)",
                      borderLeft: "3px solid #2196F3",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#546e7a",
                        fontWeight: 600,
                        display: "block",
                        mb: 0.25,
                      }}
                    >
                      Explanation:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#37474f", lineHeight: 1.4 }}
                    >
                      {" "}
                      {/* Show dictionary fallback only when it differs from the
                    context meaning (avoids duplicate lines). */}
                      {contextMeaning &&
                        wordTranslation &&
                        wordTranslation.trim().toLowerCase() !==
                          contextMeaning.trim().toLowerCase() && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              mt: 0.5,
                              color: "#78909c",
                              fontStyle: "italic",
                            }}
                          >
                            {contextMeaning ? `${contextMeaning} ` : ""}
                            {wordTranslation}
                          </Typography>
                        )}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* Actions */}
            {!loading && !error && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mt={1.25}
                gap={1}
              >
                <Button
                  size="small"
                  variant={saved ? "outlined" : "contained"}
                  color={saved ? "success" : "primary"}
                  startIcon={
                    saving ? (
                      <CircularProgress size={14} color="inherit" />
                    ) : saved ? (
                      <BookmarkAddedIcon fontSize="small" />
                    ) : (
                      <BookmarkAddIcon fontSize="small" />
                    )
                  }
                  onClick={onSave}
                  disabled={saving || saved}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 1.5,
                    flex: 1,
                  }}
                >
                  {saved ? "Saved" : saving ? "Saving…" : "Save to Memories"}
                </Button>
                <Tooltip title="Open Memories">
                  <IconButton
                    size="small"
                    onClick={() => {
                      onClose?.();
                      navigate("/memories");
                    }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Paper>
        </Fade>
      )}
    </Popper>
  );
};

export default TranslationPopover;
